import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import { useSelector } from "react-redux";
import { NoRowOverlay } from "../../table/NoRowOverlay";
import * as React from "react";
import Fab from "@mui/material/Fab";
import LinearProgress from "@mui/material/LinearProgress";
import AddIcon from "@mui/icons-material/Add";
import {
  getUsers,
  updateUser,
  resetPassword,
} from "../../../redux/actions/user";
import { editUser, updateUserStatus } from "../../../redux/actions/user";
import { store } from "../../../store";
import { ModalUser } from "../../modals/User";
import { getStudies, getStudiesBySponsor } from "../../../redux/actions/study";
import { getCenters } from "../../../redux/actions/center";
import Button from "@mui/material/Button";
import {
  showSuccessAlert,
  showWarningAlert,
  showErrorAlert,
} from "../../ui/utils/AlertUtils";
import AssignDialog from "../../ui/dialogs/AssignDialog";
import getUniqueOptions from "../../table/getUniqueOptions";
import CustomFilter from "../../table/CustomFilter";
import { useTranslation } from "react-i18next";
import LocalizedDataGrid from "../../ui/datagrid/LocalizedDataGrid";
import EditUserDialog from "../../ui/dialogs/EditUserDialog";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import { useGridApiRef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import CSVDownloader from "../../ui/CSVDownloader";

const SPACING = 50;
const createEmptyComboFilters = () => ({
  organization: "",
  role: "",
  studies: "",
  status: "",
});

const getCenterNumber = (center) => {
  if (!center || typeof center !== "object") {
    return center ?? "";
  }

  return center.centerNumber || center.center_number || "";
};

export const ScreenUsers = (props) => {
  const apiRef = useGridApiRef();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const users = useSelector((state) => state.user.users);
  const isLoading = useSelector((state) => state.user.isLoadingUsers);

  React.useEffect(() => {
    if (!Array.isArray(users)) {
      return;
    }
    // quick check for missing id fields
    const missingId = users.find(
      (u) => !u || (typeof u === "object" && u.id == null && u._id == null),
    );
  }, [users]);

  const [visibleCreateEdit, setVisibleCreateEdit] = React.useState(false);
  const [visibleEditDialog, setVisibleEditDialog] = React.useState(false);
  const [visibleDialog, setVisibleDialog] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState(null);
  const studies = useSelector((state) => state.study.studies);
  const centers = useSelector((state) => state.center.centers);
  const [selectedStudies, setSelectedStudies] = React.useState([]);
  const [selectedCenters, setSelectedCenters] = React.useState([]);
  const [name, setName] = React.useState("");
  const [firstname, setFirstname] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [userUpdated, setUserUpdated] = React.useState(false);
  const [selectedRows, setSelectedRows] = React.useState([]);
  const [organizationOptions, setOrganizationOptions] = React.useState([]);
  const [roleOptions, setRoleOptions] = React.useState([]);
  const [statusOptions, setStatusOptions] = React.useState([]);
  const [comboFilters, setComboFilters] = React.useState(
    createEmptyComboFilters,
  );

  const statusTranslations = React.useMemo(
    () => ({
      active: t("active"),
      inactive: t("deactivated"),
    }),
    [t],
  );

  React.useEffect(() => {
    if (users.length > 0) {
      // extract unique organization and role options for filters
      setOrganizationOptions(getUniqueOptions(users, "organization"));
      setRoleOptions(getUniqueOptions(users, "role"));
      setStatusOptions(getUniqueOptions(users, "status", statusTranslations));
    }
  }, [statusTranslations, users]);

  // ─── Cell/row Click Handler ───────────────────────────────────────────────────
  const handleCellClick = (params, event) => {
    navigate(`/users/${params.row.id}`, { state: { user: params.row } });
  };

  // ------------------------------------------------------------
  // Open edit dialog for a specific row (invoked by Edit button)
  // ------------------------------------------------------------
  const onEdit = (row) => {
    setSelectedUser(row);
    setVisibleEditDialog(true);
  };

  const updateComboFilter = React.useCallback((field, value) => {
    setComboFilters((currentFilters) => ({
      ...currentFilters,
      [field]: value || "",
    }));
  }, []);

  const activeFilterLabels = React.useMemo(() => {
    const labels = [];

    if (comboFilters.organization) {
      labels.push(comboFilters.organization);
    }

    if (comboFilters.role) {
      labels.push(comboFilters.role);
    }

    if (comboFilters.studies) {
      labels.push(comboFilters.studies);
    }

    if (comboFilters.status) {
      labels.push(
        statusTranslations[comboFilters.status] || comboFilters.status,
      );
    }

    return labels;
  }, [comboFilters, statusTranslations]);

  const selectedRowsLabel = React.useMemo(() => {
    if (selectedRows.length === 0) {
      return "";
    }

    const count = selectedRows.length.toLocaleString();

    if (i18n.language?.startsWith("fr")) {
      return `${count} ligne${selectedRows.length > 1 ? "s" : ""} sélectionnée${
        selectedRows.length > 1 ? "s" : ""
      }`;
    }

    return `${count} row${selectedRows.length > 1 ? "s" : ""} selected`;
  }, [i18n.language, selectedRows.length]);

  const showFilterStatus =
    activeFilterLabels.length > 0 || selectedRows.length > 0;

  const handleResetFilters = React.useCallback(() => {
    setComboFilters(createEmptyComboFilters());
    apiRef.current?.setFilterModel?.({ items: [] });
  }, [apiRef]);

  const displayedUsers = React.useMemo(() => {
    return users.filter((user) => {
      if (
        comboFilters.organization &&
        user.organization !== comboFilters.organization
      ) {
        return false;
      }

      if (comboFilters.role && user.role !== comboFilters.role) {
        return false;
      }

      if (comboFilters.status && user.status !== comboFilters.status) {
        return false;
      }

      if (comboFilters.studies) {
        const userStudies = user.studies || [];

        return userStudies.some(
          (study) => String(study.cdlStudyCode) === String(comboFilters.studies),
        );
      }

      return true;
    });
  }, [comboFilters, users]);

  const selectedUsersForExport = React.useMemo(
    () =>
      users
        .filter((user) => selectedRows.includes(user.id))
        .map((user) => {
          const userCenters =
            Array.isArray(user.centers) && user.centers.length > 0
              ? user.centers
              : user.centerMonitors || [];

          return {
            ...user,
            centers: userCenters.map(getCenterNumber).filter(Boolean),
          };
        }),
    [selectedRows, users],
  );

  React.useEffect(() => {
    const visibleIds = new Set(displayedUsers.map((row) => row.id));
    setSelectedRows((currentRows) =>
      currentRows.filter((rowId) => visibleIds.has(rowId)),
    );
  }, [displayedUsers]);

  const columns = React.useMemo(() => [
    // Edit row btn
    {
      field: "edit",
      headerName: "",
      width: 50,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          size="small"
          color="primary"
          aria-label={t("edit")}
          onClick={(e) => {
            e.stopPropagation(); // prevent DataGrid row/cell click
            onEdit(params.row);
          }}>
          <EditIcon fontSize="small" />
        </IconButton>
      ),
    },

    {
      field: "name",
      headerName: t("name"),
      flex: 1,
      renderCell: (params) => params.value.toUpperCase(),
    },

    { field: "firstname", headerName: t("firstname"), flex: 1 },

    {
      field: "organization",
      headerName: t("organization"),
      flex: 1,
      filterable: true,
      renderCell: (params) => params.value,
      filterOperators: [
        {
          label: t("organization"),
          value: "organization",
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => value === filterItem.value;
          },
          InputComponent: (props) => (
            <CustomFilter
              {...props}
              options={organizationOptions}
              item={{
                ...props.item,
                value: props.item.value || comboFilters.organization,
              }}
              applyValue={(nextItem) => {
                props.applyValue(nextItem);
                updateComboFilter("organization", nextItem.value);
              }}
            />
          ),
        },
      ],
    },

    {
      field: "role",
      headerName: t("role"),
      flex: 1,
      renderCell: (params) =>
        params.value.charAt(0).toUpperCase() +
        params.value.slice(1).toLowerCase(),
      filterable: true,
      filterOperators: [
        {
          label: t("role"),
          value: "role",
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => value === filterItem.value;
          },
          InputComponent: (props) => (
            <CustomFilter
              {...props}
              options={roleOptions}
              item={{
                ...props.item,
                value: props.item.value || comboFilters.role,
              }}
              applyValue={(nextItem) => {
                props.applyValue(nextItem);
                updateComboFilter("role", nextItem.value);
              }}
            />
          ),
        },
      ],
    },

    { field: "email", headerName: t("email"), flex: 1.4 },

    // { field: "phoneNumber", headerName: t("phoneNumber"), flex: 0.8 },

    // Added: studies column (after phone number)
    {
      field: "studies",
      headerName: t("studies"),
      flex: 1,
      sortable: false,
      filterable: true,
      renderCell: (params) => {
        const row = params.row || {};
        const list = row.studies;
        return list.map((s) => s.cdlStudyCode || s.name || s.id).join(", ");
      },
      filterOperators: [
        {
          label: t("studies"),
          value: "studies",
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) return null;
            return ({ row }) => {
              const list = row.studies;
              return list.some(
                (s) => String(s.cdlStudyCode) === String(filterItem.value),
              );
            };
          },
          InputComponent: (props) => (
            <CustomFilter
              {...props}
              // options use cdlStudyCode as the filter value
              options={(studies || []).map((s) => ({
                value: s.cdlStudyCode,
                label: s.cdlStudyCode || String(s.id),
              }))}
              item={{
                ...props.item,
                value: props.item.value || comboFilters.studies,
              }}
              applyValue={(nextItem) => {
                props.applyValue(nextItem);
                updateComboFilter("studies", nextItem.value);
              }}
            />
          ),
        },
      ],
    },

    {
      field: "status",
      headerName: t("account"),
      flex: 0.8,
      filterable: true,
      renderCell: (params) =>
        params.value === "active"
          ? t("active")
          : params.value === "inactive"
            ? t("deactivated")
            : params.value,
      filterOperators: [
        {
          label: t("account"),
          value: "status",
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => value === filterItem.value;
          },
          InputComponent: (props) => (
            <CustomFilter
              {...props}
              options={statusOptions}
              item={{
                ...props.item,
                value: props.item.value || comboFilters.status,
              }}
              applyValue={(nextItem) => {
                props.applyValue(nextItem);
                updateComboFilter("status", nextItem.value);
              }}
            />
          ),
        },
      ],
    },
  ], [
    comboFilters.organization,
    comboFilters.role,
    comboFilters.status,
    comboFilters.studies,
    organizationOptions,
    roleOptions,
    statusOptions,
    studies,
    t,
    updateComboFilter,
  ]);

  const handleDeactivateUser = () => {
    if (selectedRows.length > 0) {
      const selectedUserNames = selectedRows
        .map((id) => {
          const user = users.find((user) => user.id === id);
          return `${user.firstname} ${user.name}`;
        })
        .join(", ");

      const alertMessage = `You are about to deactivate the account${
        selectedRows.length > 1 ? "s" : ""
      } of ${selectedUserNames}. Do you really want to proceed?`;

      showWarningAlert(alertMessage).then((result) => {
        if (result.isConfirmed) {
          const deactivationPromises = selectedRows.map((id) =>
            store.dispatch(updateUserStatus(id, "inactive")),
          );
          Promise.all(deactivationPromises).then(() => {
            showSuccessAlert("Account deactivated successfully");
            setUserUpdated(true);
          });
        }
      });
    }
  };

  // Reactivate selected users
  const handleReactiveUser = () => {
    if (selectedRows.length > 0) {
      const selectedUserNames = selectedRows
        .map((id) => {
          const user = users.find((user) => user.id === id);
          return `${user.firstname} ${user.name}`;
        })
        .join(", ");

      const alertMessage = `You are about to reactivate the account${
        selectedRows.length > 1 ? "s" : ""
      } of ${selectedUserNames}. Do you want to proceed?`;

      showWarningAlert(alertMessage).then((result) => {
        if (result.isConfirmed) {
          const reactivationPromises = selectedRows.map((id) =>
            store.dispatch(updateUserStatus(id, "active")),
          );
          Promise.all(reactivationPromises).then(() => {
            showSuccessAlert("Account reactivated successfully");
            setUserUpdated(true);
          });
        }
      });
    }
  };

  const onClose = () => {
    setVisibleEditDialog(false);
  };

  const handleStudyChange = (event, studyId) => {
    if (event.target.checked) {
      setSelectedStudies((prev) => [...prev, studyId]);
    } else {
      setSelectedStudies((prev) => prev.filter((id) => id !== studyId));
    }
  };

  const handleCenterChange = (event, centerId) => {
    if (event.target.checked) {
      setSelectedCenters((prev) => [...prev, centerId]);
    } else {
      setSelectedCenters((prev) => prev.filter((id) => id !== centerId));
    }
  };

  // Handle Confirm assignment of studies/centers
  const handleConfirm = async () => {
    let updates = {};

    if (selectedUser.role === "manager") {
      updates = { sponsorStudies: selectedStudies };
    } else if (selectedUser.role === "monitor") {
      updates = { centerMonitors: selectedCenters };
    } else if (selectedUser.organization === "CDL") {
      updates = { employeeStudies: selectedStudies };
    }

    const success = await store.dispatch(
      updateUser({
        id: selectedUser.id,
        updates,
      }),
    );

    if (success) {
      setVisibleDialog(false);
      showSuccessAlert("successfully");
    } else {
      showErrorAlert("Failed to update user. Please try again.");
    }
  };

  // Set selected studies when user is selected
  React.useEffect(() => {
    if (
      selectedUser?.organization === "Sponsor" &&
      selectedUser?.sponsorStudies
    ) {
      setSelectedStudies(selectedUser.sponsorStudies.map((study) => study.id));
    }
    if (selectedUser?.organization === "CDL" && selectedUser?.employeeStudies) {
      setSelectedStudies(selectedUser.employeeStudies.map((study) => study.id));
    }
  }, [selectedUser]);

  // Set selected centers when user is selected
  React.useEffect(() => {
    if (selectedUser?.centerMonitors) {
      setSelectedCenters(
        selectedUser.centerMonitors.map((center) => center.id),
      );
    }
  }, [selectedUser]);

  // Fetch users
  React.useEffect(() => {
    store.dispatch(getUsers());
  }, []);

  React.useEffect(() => {
    if (userUpdated) {
      store.dispatch(getUsers());
      setUserUpdated(false);
    }
  }, [userUpdated]);

  // Fetch studies and centers when dialog is opened
  React.useEffect(() => {
    if (selectedUser?.role === "manager") {
      store.dispatch(getStudiesBySponsor(selectedUser.sponsorId));
    } else if (
      selectedUser?.role === "monitor" &&
      selectedUser.centerMonitors.length > 0
    ) {
      const studyId = selectedUser.centerMonitors[0].studyId; // retrieve studyId from center
      store.dispatch(getCenters(studyId));
    } else if (
      selectedUser?.role === "employee" &&
      selectedUser.organization === "CDL"
    ) {
      store.dispatch(getStudies());
    }
  }, [visibleDialog, selectedUser]);

  return (
    <Grid item xs={12}>
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
        }}>
        <div
          style={{ width: "100%", height: "90vh", position: "relative" }}>
          <LocalizedDataGrid
            apiRef={apiRef}
            slots={{
              noRowsOverlay: NoRowOverlay,
              loadingOverlay: LinearProgress,
            }}
            // onCellClick={handleCellClick}
            rows={displayedUsers}
            columns={columns}
            loading={isLoading}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 50 },
              },
            }}
            rowHeight={30}
            pageSizeOptions={[50, 100]}
            checkboxSelection
            hideFooterSelectedRowCount
            disableRowSelectionOnClick={true} // ─── DISABLE SELECTION ON ROW CLICK
            onRowSelectionModelChange={(newSelection) => {
              setSelectedRows(newSelection);
            }}
          />
          {showFilterStatus && (
            <Box
              sx={{
                position: "absolute",
                left: 16,
                bottom: 10,
                display: "flex",
                alignItems: "center",
                gap: 1,
                maxWidth: "70%",
                color: "text.secondary",
                fontSize: "0.875rem",
                zIndex: 2,
              }}>
              {activeFilterLabels.length > 0 && (
                <IconButton
                  aria-label="Reset filters"
                  onClick={handleResetFilters}
                  size="small"
                  sx={{ p: 0.25, flexShrink: 0 }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
              <Box
                component="span"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}>
                {activeFilterLabels.length > 0
                  ? `Filters: ${activeFilterLabels.join(", ")}`
                  : selectedRowsLabel}
              </Box>
              {activeFilterLabels.length > 0 && selectedRowsLabel && (
                <Box
                  component="span"
                  sx={{ color: "text.primary", flexShrink: 0 }}>
                  {selectedRowsLabel}
                </Box>
              )}
            </Box>
          )}

          {selectedRows.length > 0 && (
            <div
              style={{
                display: "flex",
                gap: 16,
                margin: "16px",
                alignItems: "center",
                height: 56, // match MUI button default height
              }}>
              <div
                style={{ height: 56, display: "flex", alignItems: "center" }}>
                <CSVDownloader
                  data={selectedUsersForExport}
                  filename={"CDLPortal_selected_accounts"}
                  name={t("selectedAccounts")}
                  includeExportMetadata
                  excludedColumns={[
                    "centerId",
                    "centerMonitors",
                    "sponsorId",
                    "sponsorStudies",
                    "employeeStudies",
                    "userStudies",
                  ]}
                  columnLabels={{ phoneNumber: "Phone" }}
                  capitalizeHeaders
                />
              </div>

              <Button
                variant="contained"
                color="secondary"
                style={{ height: 56, padding: "0 28px" }}
                onClick={handleDeactivateUser}>
                {t("deactivateAccounts")}
              </Button>
              <Button
                variant="contained"
                color="primary"
                style={{ height: 56, padding: "0 28px" }}
                onClick={handleReactiveUser}>
                {t("activateAccounts")}
              </Button>
            </div>
          )}
        </div>

        {/* User Update Form Modal */}
        <EditUserDialog
          open={visibleEditDialog}
          onClose={onClose}
          selectedUser={selectedUser}
        />

        <AssignDialog
          open={visibleDialog}
          handleClose={() => setVisibleDialog(false)}
          selectedUser={selectedUser}
          studies={studies}
          centers={centers}
          selectedStudies={selectedStudies}
          selectedCenters={selectedCenters}
          handleStudyChange={handleStudyChange}
          handleCenterChange={handleCenterChange}
          handleConfirm={handleConfirm}
        />
      </Paper>

      <Fab
        color="primary"
        aria-label="add"
        onClick={() => setVisibleCreateEdit(true)}
        style={{ position: "absolute", right: SPACING, bottom: SPACING }}>
        <AddIcon />
      </Fab>

      <ModalUser
        visible={visibleCreateEdit}
        onClose={(refresh) => {
          setVisibleCreateEdit(false);
          if (refresh) {
            store.dispatch(getUsers());
          }
        }}></ModalUser>
    </Grid>
  );
};
